import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Baby,
  Heart,
  Star,
  Sparkles,
  Clock,
  ArrowRight,
  BookOpen,
  Music,
  Palette,
  Repeat
} from "lucide-react";
import LandingNavigation from "@/components/LandingNavigation";
import Footer from "@/components/ui/footer";
import { useSEO } from "@/hooks/useSEO";

const ageFeatures = [
  {
    icon: Clock,
    title: "Perfect Length",
    description: "3-5 minute stories - just right for short attention spans"
  },
  {
    icon: Repeat,
    title: "Gentle Repetition",
    description: "Familiar patterns that comfort and aid learning"
  },
  {
    icon: Palette,
    title: "Bright Illustrations",
    description: "Colorful, simple pictures that capture attention"
  },
  {
    icon: Music,
    title: "Soothing Audio",
    description: "Calm, expressive narration perfect for little ears"
  }
];

const storyThemes = [
  { title: "Animal Friends", description: "Stories featuring cute, friendly animals", emoji: "🐰" },
  { title: "Everyday Adventures", description: "Familiar activities like bath time and bedtime", emoji: "🛁" },
  { title: "Colors & Shapes", description: "Fun explorations of the visual world", emoji: "🌈" },
  { title: "Family Love", description: "Warm stories about family and home", emoji: "🏠" },
  { title: "Nature Wonders", description: "Simple introductions to the natural world", emoji: "🌻" },
  { title: "Magical Dreams", description: "Gentle fantasy with soft, dreamlike qualities", emoji: "✨" }
];

const sampleStories = [
  {
    title: "Bunny's Bedtime",
    description: "A fluffy bunny gets ready for sleep",
    duration: "3 min"
  },
  {
    title: "The Sleepy Stars",
    description: "Stars prepare to shine for the night",
    duration: "4 min"
  },
  {
    title: "Teddy Bear's Hug",
    description: "A special goodnight hug adventure",
    duration: "3 min"
  },
  {
    title: "Little Fish Dreams",
    description: "A tiny fish drifts off in the peaceful sea",
    duration: "4 min"
  }
];

export default function Stories3to5Page() {
  useSEO({
    title: 'Stories for Ages 3-5 | Toddler Bedtime Stories | SnuggleCrafters',
    description: 'Perfect bedtime stories for toddlers and preschoolers ages 3-5. Simple, engaging AI stories with beautiful illustrations designed for young children.',
    keywords: 'toddler stories, preschool stories, stories for 3 year olds, stories for 4 year olds, stories for 5 year olds, bedtime stories toddlers',
    url: '/stories/age-3-5',
    type: 'website',
    breadcrumbs: [
      { name: 'Home', url: '/' },
      { name: 'Stories', url: '/stories' },
      { name: 'Ages 3-5', url: '/stories/age-3-5' }
    ]
  });

  return (
    <div className="min-h-screen bg-gradient-to-b from-pink-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      <LandingNavigation />

      {/* Hero Section */}
      <section className="pt-24 pb-16 px-4">
        <div className="max-w-6xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-pink-100 dark:bg-pink-900/30 text-pink-700 dark:text-pink-300 mb-6">
            <Baby className="h-4 w-4" />
            <span className="text-sm font-medium">Perfect for little ones</span>
          </div>

          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-500 bg-clip-text text-transparent">
            Bedtime Stories for Ages 3-5
          </h1>

          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto mb-8">
            Simple, sweet stories designed specifically for toddlers and preschoolers.
            Short enough to keep attention, gentle enough to inspire sweet dreams.
          </p>

          <div className="flex flex-wrap justify-center gap-4 mb-12">
            <Link href="/auth">
              <Button size="lg" className="gap-2 bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600">
                <Sparkles className="h-5 w-5" />
                Create a Toddler Story
              </Button>
            </Link>
            <Link href="/features">
              <Button size="lg" variant="outline" className="gap-2">
                <BookOpen className="h-5 w-5" />
                Learn More
              </Button>
            </Link>
          </div>

          {/* Age indicator */}
          <div className="inline-flex items-center gap-4 px-6 py-3 rounded-2xl bg-white dark:bg-gray-800 shadow-lg">
            <span className="text-4xl">👶</span>
            <div className="text-left">
              <div className="font-bold text-lg">Ages 3, 4 & 5</div>
              <div className="text-sm text-gray-500">Toddlers & Preschoolers</div>
            </div>
          </div>
        </div>
      </section>

      {/* Features for this age */}
      <section className="py-16 px-4 bg-white/50 dark:bg-gray-800/50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Designed for Little Ones</h2>
            <p className="text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
              Every story is crafted with the developmental needs of 3-5 year olds in mind.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {ageFeatures.map((feature, index) => (
              <Card key={index} className="border-0 shadow-lg text-center">
                <CardContent className="pt-6">
                  <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-pink-400 to-purple-400 flex items-center justify-center mx-auto mb-4">
                    <feature.icon className="h-7 w-7 text-white" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2">{feature.title}</h3>
                  <p className="text-gray-600 dark:text-gray-300 text-sm">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Story Themes */}
      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Favorite Themes for Toddlers</h2>
            <p className="text-gray-600 dark:text-gray-300">
              Choose themes that resonate with your little one.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {storyThemes.map((theme, index) => (
              <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-all hover:-translate-y-1 cursor-pointer">
                <CardContent className="pt-6 flex items-start gap-4">
                  <span className="text-4xl">{theme.emoji}</span>
                  <div>
                    <h3 className="text-xl font-semibold mb-1">{theme.title}</h3>
                    <p className="text-gray-600 dark:text-gray-300 text-sm">{theme.description}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Sample Stories */}
      <section className="py-16 px-4 bg-white/50 dark:bg-gray-800/50">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold mb-6">What Toddler Stories Look Like</h2>
              <p className="text-gray-600 dark:text-gray-300 mb-6">
                Our AI creates stories specifically tuned for the 3-5 age group:
              </p>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <Star className="h-5 w-5 text-pink-500 flex-shrink-0 mt-0.5" />
                  <span>Simple vocabulary appropriate for early learners</span>
                </li>
                <li className="flex items-start gap-3">
                  <Star className="h-5 w-5 text-pink-500 flex-shrink-0 mt-0.5" />
                  <span>Short sentences that are easy to follow</span>
                </li>
                <li className="flex items-start gap-3">
                  <Star className="h-5 w-5 text-pink-500 flex-shrink-0 mt-0.5" />
                  <span>Comforting, familiar scenarios</span>
                </li>
                <li className="flex items-start gap-3">
                  <Star className="h-5 w-5 text-pink-500 flex-shrink-0 mt-0.5" />
                  <span>Happy, peaceful endings</span>
                </li>
                <li className="flex items-start gap-3">
                  <Star className="h-5 w-5 text-pink-500 flex-shrink-0 mt-0.5" />
                  <span>Optional repetitive phrases for engagement</span>
                </li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-pink-100 to-purple-100 dark:from-pink-900/30 dark:to-purple-900/30 rounded-3xl p-8">
              <div className="text-center mb-6">
                <Heart className="h-12 w-12 mx-auto text-pink-500 mb-2" />
                <h3 className="text-xl font-bold">Sample Stories</h3>
              </div>
              <div className="space-y-3">
                {sampleStories.map((story, index) => (
                  <div key={index} className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm">
                    <div className="flex items-center justify-between mb-1">
                      <h4 className="font-semibold">{story.title}</h4>
                      <Badge variant="secondary" className="text-xs flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {story.duration}
                      </Badge>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-300">{story.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Other Age Groups */}
      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Stories for All Ages</h2>
            <p className="text-gray-600 dark:text-gray-300">
              Have older children too? We've got stories for them as well.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
            <Link href="/stories/age-6-8">
              <Card className="border-0 shadow-lg hover:shadow-xl transition-all hover:-translate-y-1 cursor-pointer h-full">
                <CardHeader>
                  <Badge className="w-fit bg-blue-100 text-blue-700 hover:bg-blue-100">Ages 6-8</Badge>
                  <CardTitle className="text-xl">Elementary Stories</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 dark:text-gray-300 mb-4">
                    Longer stories with more adventure for growing readers.
                  </p>
                  <span className="text-purple-600 font-medium flex items-center gap-1">
                    Explore <ArrowRight className="h-4 w-4" />
                  </span>
                </CardContent>
              </Card>
            </Link>

            <Link href="/stories/bedtime">
              <Card className="border-0 shadow-lg hover:shadow-xl transition-all hover:-translate-y-1 cursor-pointer h-full">
                <CardHeader>
                  <Badge className="w-fit bg-purple-100 text-purple-700 hover:bg-purple-100">All Ages</Badge>
                  <CardTitle className="text-xl">Calming Bedtime</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 dark:text-gray-300 mb-4">
                    Peaceful stories designed to help any child wind down.
                  </p>
                  <span className="text-purple-600 font-medium flex items-center gap-1">
                    Explore <ArrowRight className="h-4 w-4" />
                  </span>
                </CardContent>
              </Card>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 bg-gradient-to-r from-pink-500 to-purple-500 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Create Your Toddler's Story Tonight</h2>
          <p className="text-xl opacity-90 mb-8">
            A personalized bedtime story they'll ask for again and again.
          </p>
          <Link href="/auth">
            <Button size="lg" className="gap-2 bg-white text-purple-600 hover:bg-gray-100">
              <Sparkles className="h-5 w-5" />
              Start Free
              <ArrowRight className="h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
